import React from 'react';

import { useUpdatedRef } from './useUpdatedRef';

/** The current value of the boolean state */
type Value = boolean;
/** Callback to toggle the state */
type ToggleBoolean = () => void;
/** Callback to set the state to false */
type SetToFalse = () => void;
/** Callback to set the state to true */
type SetToTrue = () => void;

/**
 * This utils give the setter functions for a simple useState<boolean>.
 *
 * @param defaultValue Default value to use.
 * @param onChange Callback called only when the boolean value actually changes.
 * @return {array} A tuple following this format [value, toggleBoolean, setToFalse, setToTrue].
 */
export const useBooleanState = (
    defaultValue: boolean,
    onChange?: (value: boolean) => void,
): [Value, ToggleBoolean, SetToFalse, SetToTrue] => {
    const [value, setValue] = React.useState(defaultValue);
    // Keep `onChange` in a ref so the setters keep a stable identity even with an unstable callback
    const onChangeRef = useUpdatedRef(onChange);

    const setToFalse = React.useCallback(() => {
        setValue((previousValue) => {
            if (previousValue) {
                onChangeRef.current?.(false);
            }
            return false;
        });
    }, [onChangeRef]);
    const setToTrue = React.useCallback(() => {
        setValue((previousValue) => {
            if (!previousValue) {
                onChangeRef.current?.(true);
            }
            return true;
        });
    }, [onChangeRef]);
    const toggleBoolean = React.useCallback(
        () =>
            setValue((previousValue) => {
                const newValue = !previousValue;
                onChangeRef.current?.(newValue);
                return newValue;
            }),
        [onChangeRef],
    );

    return [value, toggleBoolean, setToFalse, setToTrue];
};
